/**
 * SummarizeButton — Room ヘッダ右上に置く「要約」ボタン.
 *
 * 設計書: docs/chatwork_killer/07_ai_voice_video.md セクション 6.1
 *
 * 振る舞い:
 *   - クリックでモーダルを開き、対象期間 (1日 / 1週間 / 全部 / 重要会議) を選ばせる.
 *   - 確定すると POST /v1/rooms/:id/summarize を呼び、結果を 1 段落 + bullets +
 *     アクションアイテムで表示.
 *   - 「タスク化」ボタンも一緒に出して、TaskExtractMenu への導線を提供 (任意).
 *
 * Props:
 *   - roomId: 対象 room
 *   - fetchMessages: モーダル open 時に呼ぶ. 期間内 message を返す callback.
 *   - locale, defaultRange は任意.
 */
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Loader2 } from "lucide-react";
import {
  fetchSummarize,
  type SummarizeResponse,
  type SummaryMessageInput,
} from "./api";
import { cn } from "@/lib/cn";

export type SummaryRange = "day" | "week" | "all" | "important";

export interface SummarizeButtonProps {
  roomId: string;
  /** モーダル「要約する」確定時に呼ばれる. 期間内 messages を返す. */
  fetchMessages: (range: SummaryRange) => Promise<SummaryMessageInput[]>;
  /** 既定 range. 未指定なら "day". */
  defaultRange?: SummaryRange;
  locale?: "ja-JP" | "en-US";
  /** カスタムボタン (slot) */
  children?: ReactNode;
  /** テスト DI */
  fetcher?: typeof fetchSummarize;
}

export function SummarizeButton(props: SummarizeButtonProps): JSX.Element {
  const { t } = useTranslation();
  const {
    roomId,
    fetchMessages,
    defaultRange = "day",
    locale = "ja-JP",
    children,
    fetcher = fetchSummarize,
  } = props;
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<SummaryRange>(defaultRange);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [result, setResult] = useState<SummarizeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(): Promise<void> {
    setStatus("loading");
    setError(null);
    try {
      const messages = await fetchMessages(range);
      if (messages.length === 0) {
        setStatus("error");
        setError(t("ai.summary.noMessages", { defaultValue: "対象期間にメッセージがありません" }) as string);
        return;
      }
      const res = await fetcher(roomId, { messages, locale, important: range === "important" }, { range });
      setResult(res);
      setStatus("ready");
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "summary failed");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setStatus("idle");
          setResult(null);
          setError(null);
        }}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded",
          "text-sm text-fg-secondary",
          "hover:bg-surface-raised hover:text-fg-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600",
        )}
        aria-label={t("ai.summary.button", { defaultValue: "要約" }) as string}
        data-testid="summarize-button"
      >
        <FileText size={16} aria-hidden />
        {children ?? (t("ai.summary.button", { defaultValue: "要約" }) as string)}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-fg-primary/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="summarize-modal-title"
          data-testid="summarize-modal"
        >
          <div className="bg-canvas rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <header className="px-4 py-3 border-b border-border-subtle flex items-center">
              <h2 id="summarize-modal-title" className="font-bold text-base">
                {t("ai.summary.title", { defaultValue: "AI 要約" }) as string}
              </h2>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-fg-muted hover:text-fg-primary"
                aria-label={t("common.close", { defaultValue: "閉じる" }) as string}
              >
                ×
              </button>
            </header>
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-fg-muted">1 回の処理では指定期間の直近最大 500 件を要約します。保存履歴の期限や月間の利用回数を制限するものではありません。</p>
              <fieldset>
                <legend className="text-sm font-medium text-fg-primary mb-2">
                  {t("ai.summary.rangeLegend", { defaultValue: "期間を選択" }) as string}
                </legend>
                <div className="flex gap-2 flex-wrap">
                  {(["day", "week", "all", "important"] as const).map((r) => (
                    <label
                      key={r}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full",
                        "border text-sm cursor-pointer",
                        range === r
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-border-subtle bg-canvas text-fg-secondary hover:bg-surface-raised",
                      )}
                    >
                      <input
                        type="radio"
                        name="summary-range"
                        value={r}
                        checked={range === r}
                        onChange={() => setRange(r)}
                        className="sr-only"
                      />
                      <span>{rangeLabel(r, locale)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <button
                type="button"
                onClick={() => void run()}
                disabled={status === "loading"}
                className={cn(
                  "px-4 py-2 rounded bg-brand-600 text-fg-inverse",
                  "hover:bg-brand-700 disabled:bg-fg-subtle",
                  "inline-flex items-center gap-2",
                )}
                data-testid="summarize-run"
              >
                {status === "loading" && <Loader2 size={14} aria-hidden className="animate-spin" />}
                {status === "loading"
                  ? (t("ai.summary.running", { defaultValue: "要約中..." }) as string)
                  : (t("ai.summary.run", { defaultValue: "要約する" }) as string)}
              </button>

              {error && (
                <div className="text-danger text-sm" role="alert">
                  {error}
                </div>
              )}

              {result && (
                <div className="space-y-3 pt-3 border-t border-border-subtle">
                  <p className="text-fg-primary leading-relaxed">{result.paragraph}</p>
                  {result.bullets.length > 0 && (
                    <ul className="list-disc pl-5 space-y-1 text-sm text-fg-secondary">
                      {result.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  )}
                  {result.action_items.length > 0 && (
                    <section
                      className="rounded bg-warning-bg p-3"
                      aria-labelledby="action-items-heading"
                    >
                      <h3
                        id="action-items-heading"
                        className="text-sm font-medium text-warning mb-1"
                      >
                        {t("ai.summary.actionItems", { defaultValue: "アクションアイテム" }) as string}
                      </h3>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-fg-primary">
                        {result.action_items.map((a, i) => (
                          <li key={i}>{a}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <footer className="text-xs text-fg-muted">
                    要約を生成しました。
                  </footer>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function rangeLabel(r: SummaryRange, locale: "ja-JP" | "en-US"): string {
  const ja: Record<SummaryRange, string> = {
    day: "今日 (24h)",
    week: "今週 (7d)",
    all: "全期間",
    important: "重要会議のみ",
  };
  const en: Record<SummaryRange, string> = {
    day: "Today (24h)",
    week: "This week (7d)",
    all: "All time",
    important: "Important only",
  };
  return locale === "ja-JP" ? ja[r] : en[r];
}

export const _internal = { rangeLabel };
